import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

gsap.registerPlugin(SplitText, ScrollTrigger)

// Duration + stagger per split type. Smaller units animate faster, otherwise a
// long heading split into chars keeps running well after it is in view.
const splitConfig = {
    lines: { duration: 0.8, stagger: 0.08 },
    words: { duration: 0.6, stagger: 0.06 },
    chars: { duration: 0.4, stagger: 0.01 }
}

// A block-level child inside the heading is treated by SplitText as one single
// line, so rich text fields (which render <p>, <br>, <strong>, ...) end up
// unsplit. We hand SplitText those blocks instead of the wrapper.
const BLOCK_SELECTOR = 'p, h1, h2, h3, h4, h5, h6, li, blockquote'

/**
 * Returns what should actually be split: the innermost block-level elements
 * with text in them, or the heading itself when it only holds inline content.
 *
 * @param {Element} heading
 * @returns {Element|Element[]}
 */
const getSplitTargets = (heading) => {
    const blocks = Array.from(heading.querySelectorAll(BLOCK_SELECTOR)).filter(
        (block) =>
            !block.querySelector(BLOCK_SELECTOR) && block.textContent.trim()
    )

    return blocks.length ? blocks : heading
}

/**
 * Masked text reveal for every [data-split="heading"] inside `root`.
 * The unit comes from data-split-reveal ("lines" | "words" | "chars"), and
 * only what is needed gets split, so we don't create DOM nodes for nothing.
 *
 * @param {Document|Element} [root] Limits the reveal to this element.
 */
export const initSplitTextReveal = (root = document) => {
    const headings = Array.from(root.querySelectorAll('[data-split="heading"]'))

    if (!headings.length) return

    const uninitialized = headings.filter(
        (heading) => heading.dataset.splitInitialized !== 'true'
    )
    if (!uninitialized.length) return

    uninitialized.forEach((heading) => {
        heading.dataset.splitInitialized = 'true'
    })

    // Reduced motion: only undo the visibility:hidden that guards against a
    // flash of unstyled text, no splitting and no animation.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        gsap.set(uninitialized, { autoAlpha: 1 })
        return
    }

    // Wait for the webfonts (or the lines get measured against the fallback
    // font) and for the native view-transition crossfade to finish, or the
    // reveal is already over by the time the user actually sees the page.
    const pageRevealFinished = window.__pageRevealFinished ?? Promise.resolve()

    Promise.all([document.fonts.ready, pageRevealFinished]).then(() => {
        uninitialized.forEach((heading) => {
            // "characters" is accepted as an alias, anything unknown is lines.
            const requested =
                heading.dataset.splitReveal === 'characters'
                    ? 'chars'
                    : heading.dataset.splitReveal
            const type = splitConfig[requested] ? requested : 'lines'
            const typesToSplit =
                type === 'lines'
                    ? ['lines']
                    : type === 'words'
                      ? ['lines', 'words']
                      : ['lines', 'words', 'chars']

            gsap.set(heading, { autoAlpha: 1 })

            SplitText.create(getSplitTargets(heading), {
                type: typesToSplit.join(', '),
                mask: 'lines', // wraps each line in an overflow:hidden div
                autoSplit: true,
                linesClass: 'line',
                wordsClass: 'word',
                charsClass: 'letter',
                onSplit: (instance) => {
                    const config = splitConfig[type]

                    // Returned so autoSplit reverts this tween before it
                    // re-splits, instead of stacking animations on resize.
                    return gsap.from(instance[type], {
                        yPercent: 110,
                        duration: config.duration,
                        stagger: config.stagger,
                        ease: 'expo.out',
                        scrollTrigger: {
                            trigger: heading,
                            start: 'top 90%',
                            once: true
                        }
                    })
                }
            })
        })
    })
}
